import { Component, OnInit } from '@angular/core';
import { CustomerService } from 'src/app/services/customer.service';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'app-analytics',
  templateUrl: './analytics.component.html',
  styleUrls: ['./analytics.component.css'],
})
export class AnalyticsComponent implements OnInit {
  analyticsData: any;
  loading: boolean = true;

  // Cohort labels mapping
  cohortLabels: any = {
    day_0_1: 'Immediate (0-1 Days)',
    days_2_3: 'Early (2-3 Days)',
    days_4_7: 'Week 1 (4-7 Days)',
    days_8_30: 'Month 1 (8-30 Days)',
    after_30_days: 'Late (>30 Days)',
    never_paid: 'Never Paid',
    scouted: 'Scouted',
    grand_total: 'Grand Total',
  };

  // Define logical order
  cohortOrder: string[] = [
    'grand_total',
    'day_0_1',
    'days_2_3',
    'days_4_7',
    'days_8_30',
    'after_30_days',
    'never_paid',
    'scouted',
  ];

  // Chart Options
  cohortDistributionOptions: any = {};
  genderDistributionOptions: any = {};
  retentionOptions: any = {};
  packagePreferenceOptions: any = {};
  datingSuccessOptions: any = {};

  // Table Data
  tableData: any[] = [];
  packageHeaders: string[] = [];

  constructor(
    private customerService: CustomerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.fetchAnalyticsData();
  }

  fetchAnalyticsData() {
    this.loading = true;
    const token = localStorage.getItem('token');

    this.customerService.getSubscriptionAnalytics(token).subscribe({
      next: (res: any) => {
        if (res.status == 200 || res.success) {
          this.analyticsData = res.data;
          this.processData();
        } else {
          this.toastr.error('Failed to load analytics data');
        }
        this.loading = false;
      },
      error: (err: any) => {
        console.error('Analytics Fetch Error', err);
        this.toastr.error('Error loading analytics data');
        this.loading = false;
      },
    });
  }

  processData() {
    this.prepareTableData();
    this.createCohortDistributionChart();
    this.createGenderDistributionChart();
    this.createRetentionChart();
    this.createPackagePreferenceChart();
    this.createDatingSuccessChart();
  }

  getOrderedKeys(): string[] {
    const dataKeys = Object.keys(this.analyticsData);
    // Return keys sorted by their index in cohortOrder, appending any unknown keys at the end
    return dataKeys.sort((a, b) => {
      const idxA = this.cohortOrder.indexOf(a);
      const idxB = this.cohortOrder.indexOf(b);
      // If both are in the order list, compare indices
      if (idxA !== -1 && idxB !== -1) return idxA - idxB;
      // If only A is in list, A comes first
      if (idxA !== -1) return -1;
      // If only B is in list, B comes first
      if (idxB !== -1) return 1;
      // If neither, keep original order (or alphabetical)
      return a.localeCompare(b);
    });
  }

  prepareTableData() {
    this.tableData = [];
    const packageSet = new Set<string>();

    // First pass: Collect all unique package names
    Object.keys(this.analyticsData).forEach((key) => {
      ['all', 'male', 'female'].forEach((segment) => {
        const pkgs = this.analyticsData[key]?.[segment]?.packages || {};
        Object.keys(pkgs).forEach((p) => packageSet.add(p));
      });
    });

    this.packageHeaders = Array.from(packageSet).sort();

    // Second pass: Create rows
    const orderedKeys = this.getOrderedKeys(); // Use ordered keys

    orderedKeys.forEach((key) => {
      const cohortLabel = this.cohortLabels[key] || key;

      ['all', 'male', 'female'].forEach((segment) => {
        const data = this.analyticsData[key]?.[segment];
        if (data) {
          const row: any = {
            cohort: cohortLabel,
            segment: segment.charAt(0).toUpperCase() + segment.slice(1),
            totalUsers: data.total_users || 0,
            activePaid: data.active_paid || 0,
            renewed: data.renewed || 0,
            droppedOff: data.dropped_off || 0,
            paidWithMatch: data.paid_with_match || 0,
            paidNoMatch: data.paid_no_match || 0,
            paidWithDate: data.paid_with_date || 0,
            paidNoDate: data.paid_no_date || 0,
            completedDates: data.completed_dates || 0,
            cancelledDates: data.cancelled_dates || 0,
            noShowDates: data.no_show_dates || 0,
            activeDates: data.active_dates || 0,
            confirmedDates: data.confirmed_dates || 0,
            paidWithDateButDropped: data.paid_with_date_but_dropped || 0,
            packages: {},
          };

          // Fill package counts
          this.packageHeaders.forEach((pkg) => {
            const val = data.packages?.[pkg] || 0;
            row.packages[pkg] = val;
          });

          this.tableData.push(row);
        }
      });
    });
  }

  createCohortDistributionChart() {
    const dataPoints = this.getOrderedKeys().map((key) => ({
      label: this.cohortLabels[key] || key,
      y: this.analyticsData[key].all.total_users,
    }));

    this.cohortDistributionOptions = {
      animationEnabled: true,
      title: {
        text: 'User Distribution by Subscription Time (all historical subsciptions, currently active and not active)',
      },
      data: [
        {
          type: 'pie',
          startAngle: 240,
          yValueFormatString: '#,##0',
          indexLabel: '{label}: {y} (#percent%)',
          dataPoints: dataPoints,
        },
      ],
    };
  }

  createGenderDistributionChart() {
    const keys = this.getOrderedKeys();
    const malePoints = keys.map((key) => ({
      label: this.cohortLabels[key] || key,
      y: this.analyticsData[key].all.males,
    }));

    const femalePoints = keys.map((key) => ({
      label: this.cohortLabels[key] || key,
      y: this.analyticsData[key].all.females,
    }));

    this.genderDistributionOptions = {
      animationEnabled: true,
      title: {
        text: 'Gender Distribution per Cohort',
      },
      subtitles: [
        {
          text: 'Notes: 1. It includes all historical active and non active subscriptions.',
          fontSize: 15,
          fontWeight: 'normal',
        },
        {
          text: '2. X axis shows when the user converts. I.e Immediate (0-1 days) means they converted within the first day of approval.',
          fontSize: 15,
          fontWeight: 'normal',
        },
      ],
      axisY: {
        title: 'Number of Users',
      },
      toolTip: {
        shared: true,
      },
      legend: {
        cursor: 'pointer',
        itemclick: function (e: any) {
          if (
            typeof e.dataSeries.visible === 'undefined' ||
            e.dataSeries.visible
          ) {
            e.dataSeries.visible = false;
          } else {
            e.dataSeries.visible = true;
          }
          e.chart.render();
        },
      },
      data: [
        {
          type: 'stackedColumn',
          name: 'Male',
          showInLegend: true,
          dataPoints: malePoints,
        },
        {
          type: 'stackedColumn',
          name: 'Female',
          showInLegend: true,
          dataPoints: femalePoints,
        },
      ],
    };
  }

  createRetentionChart() {
    const activePoints: any[] = [];
    const renewedPoints: any[] = [];
    const droppedPoints: any[] = [];

    // Filter out 'never_paid' and 'scouted' as they usually have 0 metrics
    const keys = this.getOrderedKeys().filter(
      (k) => k !== 'never_paid' && k !== 'scouted'
    );

    keys.forEach((key) => {
      const data = this.analyticsData[key].all;
      const total = data.total_users || 1;

      activePoints.push({ label: this.cohortLabels[key], y: data.active_paid });
      renewedPoints.push({ label: this.cohortLabels[key], y: data.renewed });
      droppedPoints.push({
        label: this.cohortLabels[key],
        y: data.dropped_off,
      });
    });

    this.retentionOptions = {
      animationEnabled: true,
      title: {
        text: 'Retention Status by Cohort',
      },
      axisY: {
        title: 'Users',
      },
      toolTip: {
        shared: true,
      },
      legend: {
        cursor: 'pointer',
      },
      data: [
        {
          type: 'column',
          name: 'Active Paid',
          showInLegend: true,
          dataPoints: activePoints,
        },
        {
          type: 'column',
          name: 'Renewed',
          showInLegend: true,
          dataPoints: renewedPoints,
        },
        {
          type: 'column',
          name: 'Dropped Off',
          showInLegend: true,
          dataPoints: droppedPoints,
        },
      ],
    };
  }

  createPackagePreferenceChart() {
    // Collect all package types
    const packageTypes = new Set<string>();
    Object.keys(this.analyticsData).forEach((key) => {
      if (key === 'never_paid' || key === 'scouted') return;
      const pkgs = this.analyticsData[key].all.packages || {};
      Object.keys(pkgs).forEach((p) => packageTypes.add(p));
    });

    const keys = this.getOrderedKeys().filter(
      (k) => k !== 'never_paid' && k !== 'scouted'
    );

    const dataSeries = Array.from(packageTypes).map((pkgName) => {
      const dps: any[] = [];
      keys.forEach((key) => {
        const val = this.analyticsData[key].all.packages?.[pkgName] || 0;
        dps.push({ label: this.cohortLabels[key], y: val });
      });
      return {
        type: 'stackedColumn100',
        name: pkgName,
        showInLegend: true,
        dataPoints: dps,
      };
    });

    this.packagePreferenceOptions = {
      animationEnabled: true,
      title: {
        text: 'Package Preferences by Cohort',
      },
      toolTip: {
        shared: true,
      },
      legend: {
        cursor: 'pointer',
      },
      data: dataSeries,
    };
  }

  createDatingSuccessChart() {
    const completedPoints: any[] = [];
    const cancelledPoints: any[] = [];
    const noShowPoints: any[] = [];

    const keys = this.getOrderedKeys().filter(
      (k) => k !== 'never_paid' && k !== 'scouted'
    );

    keys.forEach((key) => {
      const data = this.analyticsData[key].all;
      completedPoints.push({
        label: this.cohortLabels[key],
        y: data.completed_dates,
      });
      cancelledPoints.push({
        label: this.cohortLabels[key],
        y: data.cancelled_dates,
      });
      noShowPoints.push({
        label: this.cohortLabels[key],
        y: data.no_show_dates,
      });
    });

    this.datingSuccessOptions = {
      animationEnabled: true,
      title: {
        text: 'Dating Outcomes by Cohort',
      },
      axisY: {
        title: 'Count',
      },
      toolTip: {
        shared: true,
      },
      data: [
        {
          type: 'column',
          name: 'Completed',
          showInLegend: true,
          dataPoints: completedPoints,
        },
        {
          type: 'column',
          name: 'Cancelled',
          showInLegend: true,
          dataPoints: cancelledPoints,
        },
        {
          type: 'column',
          name: 'No Show',
          showInLegend: true,
          dataPoints: noShowPoints,
        },
      ],
    };
  }
}
